import type { SupportedLocale } from './constants';

/**
 * A curated, human-facing name for a locale in two registers.
 */
export interface LocaleLabel {
  /** English name, for English-language UI and admin surfaces. */
  english: string;
  /** The language's own name, for user-facing pickers. */
  endonym: string;
  /**
   * The endonym's parts, for two-line pickers that show the language above the
   * region — the shape joy-web's guest-site selector and marcom's language menu
   * render, and the convention most large consumer sites use.
   *
   * `endonym` is NOT always `language (region)`: for the five English variants
   * it is a single phrase like "British English", so these are curated rather
   * than parsed out of it.
   */
  language: string;
  region: string;
}

/**
 * Curated locale labels, replacing the language-label maps hand-maintained
 * in each app repo's language switcher (e.g. microfrontend's
 * `LANGUAGE_LABELS` in `src/i18n/utils.ts`). When a locale is added to
 * `SUPPORTED_LOCALES` without a matching entry here, the `Record<SupportedLocale,
 * LocaleLabel>` annotation makes that a compile error — the same guarantee
 * `TRANSLATABLE_LOCALES` gives translation targets, applied to display names.
 *
 * These are deliberately NOT copied from `@withjoy/translate`'s
 * `languages.ts`. Those names serve LLM prompts, where "French" for both
 * `fr-FR` and `fr-CA` is tolerable ambiguity; here it would render two
 * indistinguishable entries in a language picker. Labels live in
 * `@withjoy/locale` — not `@withjoy/translate` — because it is a runtime
 * dependency of the app repos that render pickers, while `@withjoy/translate`
 * is a devDependency and unreachable from UI code.
 *
 * Key order intentionally matches `SUPPORTED_LOCALES` (not alphabetical).
 * That order is already semantic there — the first entry per base language
 * wins in resolution — and a switcher iterating `Object.entries(LOCALE_LABELS)`
 * inherits the same sensible grouping (English variants together, etc.)
 * for free.
 *
 * For the five English variants, `english` and `endonym` are identical
 * on purpose, not a copy-paste slip: the language being labeled *is*
 * English, so its own name and its English name are the same string.
 */
export const LOCALE_LABELS: Record<SupportedLocale, LocaleLabel> = {
  'en-US': {
    english: 'American English',
    endonym: 'American English',
    language: 'English',
    region: 'United States',
  },
  'en-GB': {
    english: 'British English',
    endonym: 'British English',
    language: 'English',
    region: 'United Kingdom',
  },
  'en-AU': {
    english: 'Australian English',
    endonym: 'Australian English',
    language: 'English',
    region: 'Australia',
  },
  'en-CA': {
    english: 'Canadian English',
    endonym: 'Canadian English',
    language: 'English',
    region: 'Canada',
  },
  'en-SG': {
    english: 'Singaporean English',
    endonym: 'Singaporean English',
    language: 'English',
    region: 'Singapore',
  },
  'de-DE': {
    english: 'German',
    endonym: 'Deutsch',
    language: 'Deutsch',
    region: 'Deutschland',
  },
  'es-ES': {
    english: 'European Spanish',
    endonym: 'Español (España)',
    language: 'Español',
    region: 'España',
  },
  'es-US': {
    english: 'US Spanish',
    endonym: 'Español (Estados Unidos)',
    language: 'Español',
    region: 'Estados Unidos',
  },
  'fr-FR': {
    english: 'French',
    endonym: 'Français',
    language: 'Français',
    region: 'France',
  },
  'fr-CA': {
    english: 'Canadian French',
    endonym: 'Français canadien',
    language: 'Français',
    region: 'Canada',
  },
  'it-IT': {
    english: 'Italian',
    endonym: 'Italiano',
    language: 'Italiano',
    region: 'Italia',
  },
  'ja-JP': {
    english: 'Japanese',
    endonym: '日本語',
    language: '日本語',
    region: '日本',
  },
};

/**
 * Returns a display label for `locale`.
 *
 * Defaults to `'endonym'` because the primary consumer is a user-facing
 * language picker: a reader who doesn't read English still needs to
 * recognize their own language in the list. Pass `'english'` explicitly
 * for English-language UI and admin surfaces where the English name is
 * more useful (e.g. internal tooling, logs).
 *
 * `'language'` and `'region'` return the endonym's parts, for two-line
 * pickers that stack the language over the region — see `LocaleLabel`.
 *
 * `locale` must already be a `SupportedLocale`. The realistic caller — a picker
 * labelling an event's stored locale — reads that value across an untyped
 * boundary (DB row, API field, query param), where TypeScript can't help. Run
 * it through `resolveSupportedLocale()` first; that function owns the
 * untrusted-string-to-SupportedLocale conversion, including alias handling and
 * the fallback policy:
 *
 * ```ts
 * getLocaleLabel(resolveSupportedLocale(event.locale), 'english');
 * ```
 *
 * @throws if `locale` is not a supported locale. Deliberately louder than
 * falling back to `I18N_FALLBACK_LOCALE`: labelling an unrecognized locale
 * "American English" is a wrong answer indistinguishable from a right one, and
 * it would put a second, invisible fallback policy here alongside the explicit
 * one in `resolveSupportedLocale`. Fail where the mistake is, not three screens
 * later in a picker showing the wrong language.
 */
export const getLocaleLabel = (
  locale: SupportedLocale,
  display: 'endonym' | 'english' | 'language' | 'region' = 'endonym',
): string => {
  // hasOwnProperty.call, not a truthiness check on the lookup: 'toString',
  // 'constructor', 'valueOf' and '__proto__' all resolve to truthy inherited
  // members of Object.prototype, so a plain `if (!LOCALE_LABELS[locale])` lets
  // them through and the function returns undefined despite its `string`
  // return type. Same reason createTranslateConfig's preset guard uses it.
  // .call rather than Object.hasOwn: the repo's engine floor is Node >=16 and
  // Object.hasOwn only exists from 16.9 — matching resolve-supported-locale.ts.
  const label = Object.prototype.hasOwnProperty.call(LOCALE_LABELS, locale)
    ? LOCALE_LABELS[locale]
    : undefined;
  if (!label) {
    throw new Error(
      `getLocaleLabel: unsupported locale ${JSON.stringify(locale)}. ` +
        `Resolve untrusted input with resolveSupportedLocale() before calling this.`,
    );
  }
  return label[display];
};
